/**
 * @voltxampere/presets — Zener voltage regulator (#15)
 *
 * D2 deviation: the engine ships only the forward-bias Shockley
 * diode model (Sprint 5). Reverse-breakdown — i.e. real Zener
 * regulation — is scheduled for Sprint 22+. This preset's circuit
 * loads correctly (the schema-symbol-id alias `zener → diode`
 * resolves through the Sprint 16 alias table, see ADR not-yet-
 * filed in `register.ts`), but it does NOT regulate at the rated
 * V_z. We keep the preset structurally accurate so the dialog
 * surfaces a real Zener-regulator topology, and ship `expectedValues
 * = []` so the runtime test suite skips numerical assertion. The
 * `note` field surfaces the deferral on the preset card.
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. V1 vertical at
 * left, Rs horizontal across the top rail, Dz vertical mid-right
 * shunting cathode-up (so the cathode at `vout` sits above the
 * anode at `gnd`), and RL vertical at far-right as the load. Body
 * centers rounded to 20-px grid.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const zenerRegulator = {
    id: "zener-regulator",
    name: { tr: "Zener Diyot Regülatörü", en: "Zener Voltage Regulator" },
    category: "power",
    difficulty: 4,
    description: {
        tr: "Zener diyot ile sabit gerilim regülasyonu. V1=15V, Rs=100Ω, Vz=5.1V, RL=1kΩ.",
        en: "Constant-voltage regulation via a Zener diode. V1=15V, Rs=100Ω, Vz=5.1V, RL=1kΩ.",
    },
    circuit: makeCircuit({
        slot: 15,
        name: "Zener Regulator",
        description: "Shunt Zener regulator — structural-only until Sprint 22+",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vin" }),
            makeNet({ name: "vout" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 15 },
                terminals: { pos: "net_vin", neg: "net_gnd" },
                label: "V1",
                position: { x: -240, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "Rs",
                type: "resistor",
                params: { resistance: 100 },
                terminals: { p1: "net_vin", p2: "net_vout" },
                label: "Rs",
                position: { x: -100, y: -80 },
                rotation: 0,
            }),
            makeComponent({
                // Sprint 78 fix — fixture was authored as `type: "diode"`
                // with a stale "reverse breakdown not yet modelled" comment.
                // The canonical zener component DOES model reverse
                // breakdown via `breakdownVoltage` (see
                // packages/engine/src/stamps/nonlinear/zener.ts line 49,
                // packages/components/src/types/zener.ts line 46). 5.1 V is
                // the schema default. Kol E Tier-2 T2-2 NET_MISSING verdict
                // unblocks because the `breakdownVoltage` parameter now
                // surfaces on the component.
                id: "Dz",
                type: "zener",
                params: { breakdownVoltage: 5.1 },
                terminals: { anode: "net_gnd", cathode: "net_vout" },
                label: "Dz",
                // rot 270 places the cathode pin at (40, -20) (above the
                // body center) and the anode pin at (40, 20) (below) —
                // K is on the `vout` rail, A heads down to `gnd`.
                position: { x: 40, y: 0 },
                rotation: 270,
            }),
            makeComponent({
                id: "RL",
                type: "resistor",
                params: { resistance: 1_000 },
                terminals: { p1: "net_vout", p2: "net_gnd" },
                label: "RL",
                position: { x: 140, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -240, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // vin rail: V1.pos (-240, -10) → Rs.p1 (-120, -80)
            {
                id: "w_vin",
                netId: "net_vin",
                path: [
                    { x: -240, y: -10 },
                    { x: -240, y: -80 },
                    { x: -120, y: -80 },
                ],
                style: "orthogonal",
            },
            // vout rail: Rs.p2 (-80, -80) → Dz.K (40, -20) → RL.p1 (140, -20)
            {
                id: "w_vout_top",
                netId: "net_vout",
                path: [
                    { x: -80, y: -80 },
                    { x: 40, y: -80 },
                    { x: 40, y: -20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_vout_right",
                netId: "net_vout",
                path: [
                    { x: 40, y: -20 },
                    { x: 140, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg A: V1.neg (-240, 10) → GND.p (-240, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -240, y: 10 },
                    { x: -240, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg B: Dz.A (40, 20) → RL.p2 (140, 20) → bottom rail → GND.p
            {
                id: "w_gnd_dz_rl",
                netId: "net_gnd",
                path: [
                    { x: 40, y: 20 },
                    { x: 140, y: 20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd_bottom",
                netId: "net_gnd",
                path: [
                    { x: 140, y: 20 },
                    { x: 140, y: 110 },
                    { x: -240, y: 110 },
                    { x: -240, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [],
    learningGoals: [
        {
            tr: "Zener kırılma bölgesinde sabit gerilim üretir — paralel regülatör topolojisi.",
            en: "Zener in breakdown produces constant voltage — shunt-regulator topology.",
        },
        {
            tr: "Series resistor Rs Zener akımını sınırlar; yük akımı kayıpları regülasyonu bozmamalı.",
            en: "Series resistor Rs limits Zener current; load draw must not break regulation.",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, §1.6"],
    note: {
        tr: "Mevcut motor ters yönlü diyodu sızıntı akımıyla modelleyemiyor. Sprint 22+ Zener kırılma modeli bu preset'in sayısal doğrulamasını açacak.",
        en: "Current engine cannot model reverse-biased diode breakdown. Sprint 22+ Zener model will enable runtime verification for this preset.",
    },
};
//# sourceMappingURL=zener-regulator.js.map